import React, { createContext, FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { visitIQCategories, visitIQGeoFilters } from '~/constants';
import { visitIQPulseOrg } from '~/constants/localStorage';
import { useAppContext } from '~/contexts/AppContext';
import { useDebounce } from '~/hooks/useDebounce';
import i18n from '~/locale';
import { AudiencePulseInfo, Permission, PulseNode, usePulseNodesQuery } from '~/types/api.graphql';
import { ExtendedPulseNode } from '~/views/audiences/NewPulseAudience/types';
import { pulseItemsToPulseNodes } from '~/views/audiences/NewPulseAudience/utils/graphql';

import { useAudienceContext } from './AudienceContext';
import { useSessionContext } from './SessionContext';

const MAX_TOPICS_LIMIT = 50;
const MAX_TOPICS_LIMIT_FOR_ADMINS = 100;

interface SignalContextProps {
  displayCategories: ExtendedPulseNode[];
  loadingCategories: boolean;
  selectedCategory: SelectedCategory;
  selectedTopics: PulseNode[];
  setSelectedTopics: (topics: PulseNode[]) => void;
  setSelectedCategory: (category: SelectedCategory) => void;
  navigateToCategory: (category: SelectedCategory, clearSearch?: boolean) => void;
  toggleTopic: (topic: PulseNode) => void;
  clearPulseAudienceCreationLocalStorage: () => void;
  selectedAmountOfSignals: number;
  getSelectedTopicsCount: (category: PulseNode) => number;
  loadNextPage: () => Promise<void>;
  hasMorePages: boolean;
  loadingNextPage: boolean;
  setSearchQuery: (query: string) => void;
  searchQuery: string;
  isTopicLimitReached: boolean;
  amountOfBubblesInLevel: number;
  resetState: () => void;
  isAtTopicLevel: boolean;
  debouncedSearchQuery: string;
  simpleLoadingIndicator: boolean;
  setSimpleLoadingIndicator: (indicator: boolean) => void;
  abortCurrentRequests: () => void;
  maxTopicsLimit: number;
  searchScope: SelectedCategory;
  setSearchScope: (scope: SelectedCategory) => void;
  searchScopeOptions: SelectedCategory[];
  confirmSearchScope: (scope: SelectedCategory) => void;
}

export const ROOT_CATEGORY_ID = 'root';
interface SignalProviderProps {
  children: ReactNode;
}

const emptyPulseNode: ExtendedPulseNode = {
  isShimmer: true,
  signalCount: 0,
  name: '',
  paths: [],
  id: '',
};

const SignalContext = createContext<SignalContextProps>({
  displayCategories: [],
  loadingCategories: false,
  selectedCategory: null,
  selectedTopics: [],
  setSelectedCategory: null,
  navigateToCategory: null,
  toggleTopic: null,
  clearPulseAudienceCreationLocalStorage: null,
  selectedAmountOfSignals: 0,
  getSelectedTopicsCount: null,
  loadNextPage: () => null,
  hasMorePages: false,
  loadingNextPage: false,
  setSearchQuery: null,
  searchQuery: '',
  setSelectedTopics: null,
  isTopicLimitReached: false,
  amountOfBubblesInLevel: 0,
  resetState: null,
  isAtTopicLevel: false,
  debouncedSearchQuery: '',
  simpleLoadingIndicator: false,
  setSimpleLoadingIndicator: null,
  abortCurrentRequests: null,
  maxTopicsLimit: MAX_TOPICS_LIMIT,
  searchScope: null,
  setSearchScope: null,
  searchScopeOptions: [],
  confirmSearchScope: null,
});

export const PAGE_ITEMS_COUNT = 55;
export const MAX_LEVELS_OF_PATHS = 3;

type SelectedCategory = Omit<PulseNode, 'signalCount'> | null;

export const SignalProvider: FC<SignalProviderProps> = ({ children }) => {
  const { me, isOrgAdmin } = useSessionContext();
  const { showBottomToast } = useAppContext();
  const [categories, setCategories] = useState<ExtendedPulseNode[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<SelectedCategory>(null);
  const [selectedTopics, setSelectedTopics] = useState<PulseNode[]>([]);
  const [isInitialized, setInitialized] = useState(false);
  const [endCursor, setEndCursor] = useState('');
  const [simpleLoadingIndicator, setSimpleLoadingIndicator] = useState(false);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchScope, setSearchScope] = useState<SelectedCategory>(null);
  const [searchScopeOptions, setSearchScopeOptions] = useState<SelectedCategory[]>([]);
  const [amountOfBubblesInLevel, setAmountOfBubblesInLevel] = useState(0);
  const { audienceId: parentAudienceId } = useParams();
  const categoriesLocalStorage = parentAudienceId ? `${visitIQCategories}-${parentAudienceId}` : visitIQCategories;
  const [hasSavedTopicsDraft, setHasSavedTopicsDraft] = useState(false);
  const { audienceData } = useAudienceContext();

  const maxTopicsLimit = useMemo(() => {
    const hasSuperOwnerRole = me?.userRoles?.some((userRole) =>
      userRole.assignedRole?.permissions?.includes(Permission.SuperOwner),
    );

    return isOrgAdmin || hasSuperOwnerRole ? MAX_TOPICS_LIMIT_FOR_ADMINS : MAX_TOPICS_LIMIT;
  }, [me, isOrgAdmin]);

  // Create a single AbortController that we'll expose
  const abortControllerRef = useRef<AbortController>(new AbortController());
  // Track if we just navigated to prevent double reset
  const justNavigatedRef = useRef(false);
  // Track previous searchQuery to detect transitions
  const prevSearchQueryRef = useRef('');
  // Remember where the user was before searching, to restore on clear
  const categoryBeforeSearchRef = useRef<SelectedCategory>(null);
  // Once the edit-mode prefill below has run, never run it again for this mount, even if the
  // user then clears every topic, which persists an empty selectedTopics indistinguishable from
  // "no draft yet" (see hasSavedTopicsDraft below).
  const hasPrefilledTopicsRef = useRef(false);

  // Export a function to abort current requests and create a new controller
  const abortCurrentRequests = useCallback(() => {
    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      // Create new controller for future requests
      abortControllerRef.current = new AbortController();

      // Reset pagination state to force new queries
      setEndCursor('');
      setHasMorePages(true);
      setSimpleLoadingIndicator(false);
      setLoadingNextPage(false);
    }
  }, []);

  const debouncedSearchQuery = useDebounce(searchQuery, 850) as string;
  // Use GraphQL query for pagination
  const {
    fetchMore,
    loading: loadingCategories,
    data: pulseQueryData,
  } = usePulseNodesQuery({
    variables: {
      first: PAGE_ITEMS_COUNT,
      // Use immediate searchQuery for parentPaths logic to avoid race condition during breadcrumb navigation
      parentPaths: searchQuery ? searchScope?.paths || [] : selectedCategory ? selectedCategory.paths : [],
      // Use immediate searchQuery when empty to avoid blink, otherwise use debounced
      searchQuery: searchQuery === '' ? '' : debouncedSearchQuery,
    },
    nextFetchPolicy: 'network-only',
    fetchPolicy: 'network-only',
    skip: endCursor !== '',
    context: {
      fetchOptions: {
        signal: abortControllerRef.current.signal,
      },
    },
  });

  useEffect(() => {
    if (pulseQueryData) {
      setAmountOfBubblesInLevel(pulseQueryData?.pulseNodes?.totalCount || 0);
      const newNodes = pulseQueryData?.pulseNodes?.nodes || [];
      // Only update categories if we have real nodes to show
      if (newNodes.length > 0) {
        setCategories(newNodes);
        setHasMorePages(pulseQueryData?.pulseNodes?.pageInfo?.hasNextPage || false);
        setEndCursor(pulseQueryData?.pulseNodes?.pageInfo?.endCursor || '');
      }
    }
  }, [pulseQueryData]);

  // Improve the shimmer bubble creation function
  const createShimmerBubbles = useCallback(
    (count: number, prefix = '') => {
      return Array.from({ length: count }).map((_, index) => ({
        ...emptyPulseNode,
        id: `shimmer-${prefix}${index}-${selectedCategory?.id || ''}-${Math.random().toString(36).substring(2, 9)}`,
      }));
    },
    [selectedCategory],
  );

  useEffect(() => {
    // Re-runs if categoriesLocalStorage changes (i.e. parentAudienceId changes without this
    // provider remounting), so switching which audience is being edited doesn't leak the
    // previous audience's selection into the new one.
    const savedState = JSON.parse(localStorage.getItem(categoriesLocalStorage) || '{}');
    setSelectedCategory((savedState.selectedCategory as SelectedCategory) || null);
    setSelectedTopics((savedState.selectedTopics as PulseNode[]) || []);
    // Only a non-empty saved selection counts as a draft worth protecting from the edit-mode
    // prefill below. An empty one is what Cancel leaves behind (clearPulseAudienceCreationLocalStorage
    // removes the key, then resets the selection, which lets the persist effect write [] straight
    // back), and treating that as a draft would suppress the prefill on every later visit.
    setHasSavedTopicsDraft(!!savedState.selectedTopics?.length);
    hasPrefilledTopicsRef.current = false;

    setInitialized(true);
  }, [categoriesLocalStorage]);

  // Editing a Pulse audience: prefill selectedTopics from the audience's stored criteria,
  // unless the user already has an in-progress local draft for this audience.
  useEffect(() => {
    if (!isInitialized || !parentAudienceId || hasSavedTopicsDraft || hasPrefilledTopicsRef.current) return;
    if (audienceData?.audience?.id !== parentAudienceId) return;
    const items = (audienceData.audience.info as AudiencePulseInfo)?.items;
    if (!items?.length) return;
    hasPrefilledTopicsRef.current = true;
    setSelectedTopics(pulseItemsToPulseNodes(items));
  }, [audienceData, parentAudienceId, isInitialized, hasSavedTopicsDraft]);

  useEffect(() => {
    if (!isInitialized) return;
    localStorage.setItem(
      categoriesLocalStorage,
      JSON.stringify({
        selectedCategory,
        selectedTopics,
      }),
    );
  }, [selectedCategory, selectedTopics]);

  const resetState = useCallback(() => {
    setLoadingNextPage(false);
    setSimpleLoadingIndicator(false);
    setEndCursor('');
    setHasMorePages(false);
    setCategories([]);
    setAmountOfBubblesInLevel(0);
  }, []);

  const confirmSearchScope = useCallback(
    (scope: SelectedCategory) => {
      const isSameScope = scope?.id === searchScope?.id;

      setSearchScope(scope);
      // Any scope change updates the restore point (dropdown pick, breadcrumb click, etc.)
      categoryBeforeSearchRef.current = scope;

      // Recompute options based on new scope
      if (scope) {
        const newOptions: SelectedCategory[] = [];
        if (scope.paths.length === 2) {
          const parentCat: SelectedCategory = {
            id: scope.paths[0],
            name: scope.paths[0],
            paths: [scope.paths[0]],
          };
          newOptions.push(scope, parentCat, null);
        } else if (scope.paths.length === 1) {
          newOptions.push(scope, null);
        }
        setSearchScopeOptions(newOptions);
      } else {
        // "Everywhere" = root level, no scoping needed
        setSearchScopeOptions([]);
      }

      if (!isSameScope) {
        resetState();
        setSelectedCategory(null);
      }
    },
    [resetState, searchScope],
  );

  useEffect(() => {
    // Don't reset if we just navigated (category effect already did it)
    if (justNavigatedRef.current) {
      justNavigatedRef.current = false;
      return;
    }
    resetState();
    if (debouncedSearchQuery) {
      setSelectedCategory(null);
    }
  }, [debouncedSearchQuery]);

  // Initialize/cleanup search scope when searchQuery transitions
  useEffect(() => {
    const wasEmpty = prevSearchQueryRef.current === '';
    const isEmpty = searchQuery === '';
    prevSearchQueryRef.current = searchQuery;

    if (wasEmpty && !isEmpty) {
      // Transitioning from empty to non-empty: capture scope from current category
      const currentCat = selectedCategory;
      categoryBeforeSearchRef.current = currentCat;
      // Clear stale pagination so query fires immediately with the default scope
      setEndCursor('');
      setCategories([]);
      if (currentCat) {
        setSearchScope(currentCat);
        if (currentCat.paths.length === 2) {
          // Subcategory: options are [subcategory, category, all]
          const parentCat: SelectedCategory = {
            id: currentCat.paths[0],
            name: currentCat.paths[0],
            paths: [currentCat.paths[0]],
          };
          setSearchScopeOptions([currentCat, parentCat, null]);
        } else if (currentCat.paths.length === 1) {
          // Category: options are [category, all]
          setSearchScopeOptions([currentCat, null]);
        } else {
          setSearchScopeOptions([]);
        }
      } else {
        // At root: no scope options, search immediately
        setSearchScope(null);
        setSearchScopeOptions([]);
      }
    } else if (!wasEmpty && isEmpty) {
      // Transitioning from non-empty to empty: restore to original category before search
      const originalCategory = categoryBeforeSearchRef.current;
      setSearchScope(null);
      setSearchScopeOptions([]);
      categoryBeforeSearchRef.current = null;
      if (originalCategory) {
        // Navigate back to where the user was standing before searching
        justNavigatedRef.current = true;
        setSelectedCategory(originalCategory);
      } else {
        // Was at root: just reset to show all categories
        resetState();
      }
    }
  }, [searchQuery]);

  // Reset state when category changes (but not during search)
  useEffect(() => {
    if (!searchQuery) {
      resetState();
    }
  }, [selectedCategory]);

  // Ensure loadNextPage can properly re-trigger when needed
  const loadNextPage = useCallback(async () => {
    if (!hasMorePages || loadingNextPage || loadingCategories) return;

    // Add a uniqueness factor to ensure Apollo treats this as a new request
    const uniqueRequestId = Date.now().toString();

    setLoadingNextPage(true);
    try {
      const response = await fetchMore({
        variables: {
          first: PAGE_ITEMS_COUNT,
          after: endCursor,
          // Use immediate searchQuery for parentPaths logic to avoid race condition
          parentPaths: searchQuery ? searchScope?.paths || [] : selectedCategory ? selectedCategory.paths : [],
          searchQuery: searchQuery === '' ? '' : debouncedSearchQuery,
          _requestId: uniqueRequestId, // Apollo ignores this but it makes the variables object unique
        },
        context: {
          fetchOptions: {
            signal: abortControllerRef.current.signal,
          },
        },
      });

      const newNodes = response.data?.pulseNodes?.nodes || [];
      // Only update if we have real nodes
      if (newNodes.length > 0) {
        setCategories((prev) => [...prev, ...newNodes]);
        setHasMorePages(response.data?.pulseNodes?.pageInfo?.hasNextPage || false);
        setEndCursor(response.data?.pulseNodes?.pageInfo?.endCursor || '');
      }
    } finally {
      setLoadingNextPage(false);
      setSimpleLoadingIndicator(false);
    }
  }, [endCursor, hasMorePages, loadingNextPage, loadingCategories, fetchMore, debouncedSearchQuery, selectedCategory]);

  const isTopicLimitReached = useMemo(() => {
    return selectedTopics.length >= maxTopicsLimit;
  }, [selectedTopics]);

  const toggleTopic = (topic: PulseNode) => {
    setSelectedTopics((prevTopics) => {
      const topicExists = prevTopics.some((t) => t.id === topic.id);

      // If topic exists, we're removing it, so that's always allowed
      if (topicExists) {
        return prevTopics.filter((t) => t.id !== topic.id);
      } else {
        // Only add if it's a leaf node (topic) and we haven't reached the limit
        // Topics have 3 path segments (e.g., ["Animal Lovers", "Dogs", "Dog Lovers"])
        if (topic.paths.length === MAX_LEVELS_OF_PATHS) {
          if (prevTopics.length >= maxTopicsLimit) {
            // Show toast notification when limit is reached
            showBottomToast(i18n.t('newPulseAudience.topicLimitReachedTitle'));
            return prevTopics; // Don't add the new topic
          }

          // Show toast notification when limit is reached
          if (prevTopics.length === maxTopicsLimit - 1) {
            showBottomToast(i18n.t('newPulseAudience.topicLimitReachedTitle'));
          }
          return [topic, ...prevTopics];
        }
        return prevTopics;
      }
    });
  };

  const getSelectedTopicsCount = (category: PulseNode): number => {
    // Count topics based on path hierarchy
    return selectedTopics.filter((topic) => {
      // Get the path length we need to compare
      // - Categories have 1 path segment
      // - Subcategories have 2 path segments
      // - Topics have 3 path segments
      const pathLength = category.paths.length;

      // Compare the paths up to the category's depth
      const topicPath = topic.paths.slice(0, pathLength).join('|');
      const categoryPath = category.paths.join('|');

      return topicPath === categoryPath;
    }).length;
  };

  const selectedAmountOfSignals = useMemo(() => {
    // Only count signals from fully selected topics (path length of 3)
    return selectedTopics
      .filter((topic) => topic.paths.length === MAX_LEVELS_OF_PATHS)
      .reduce((acc, topic) => acc + topic.signalCount, 0);
  }, [selectedTopics]);

  const navigateToCategory = useCallback(
    (category: SelectedCategory, clearSearch = false) => {
      abortCurrentRequests();
      if (clearSearch) {
        setSearchQuery('');
        // Mark that we just navigated so debounce effect doesn't reset again
        justNavigatedRef.current = true;
      }
      setSelectedCategory(category);
    },
    [abortCurrentRequests],
  );

  const clearPulseAudienceCreationLocalStorage = () => {
    const geoFiltersLocalStorage = parentAudienceId
      ? `${visitIQGeoFilters}-pulse-${parentAudienceId}`
      : `${visitIQGeoFilters}-pulse`;
    localStorage.removeItem(categoriesLocalStorage);
    localStorage.removeItem(geoFiltersLocalStorage);
    localStorage.removeItem(visitIQPulseOrg);
    setSelectedCategory(null);
    setSelectedTopics([]);
  };

  const displayCategories = useMemo(() => {
    if (loadingCategories && categories.length === 0) {
      return createShimmerBubbles(PAGE_ITEMS_COUNT, 'initial-');
    }

    // Already have items but loading more
    if (loadingNextPage) {
      // Return existing categories plus shimmer placeholders at the end
      return [...categories, ...createShimmerBubbles(PAGE_ITEMS_COUNT, 'next-')];
    }

    // Normal state - just return the categories
    return categories;
  }, [categories, loadingCategories, loadingNextPage, createShimmerBubbles]);

  const isAtTopicLevel = useMemo(() => {
    return selectedCategory?.paths.length === MAX_LEVELS_OF_PATHS - 1;
  }, [selectedCategory]);

  return (
    <SignalContext.Provider
      value={{
        displayCategories,
        loadingCategories,
        selectedCategory,
        selectedTopics,
        selectedAmountOfSignals,
        setSelectedCategory,
        navigateToCategory,
        toggleTopic,
        clearPulseAudienceCreationLocalStorage,
        getSelectedTopicsCount,
        loadNextPage,
        hasMorePages,
        loadingNextPage,
        setSearchQuery,
        searchQuery,
        setSelectedTopics,
        isTopicLimitReached,
        amountOfBubblesInLevel,
        resetState,
        isAtTopicLevel,
        debouncedSearchQuery,
        simpleLoadingIndicator,
        setSimpleLoadingIndicator,
        abortCurrentRequests,
        maxTopicsLimit,
        searchScope,
        setSearchScope,
        searchScopeOptions,
        confirmSearchScope,
      }}
    >
      {children}
    </SignalContext.Provider>
  );
};

export const useSignalContext = (): SignalContextProps => {
  return React.useContext(SignalContext);
};
